import { useEffect, useRef, useState } from 'react';

export interface OrgSwitcherProps {
  orgs: Record<string, { label: string; badge: string; color: string }>;
  activeOrgId: string;
  orgIds?: string[];
  onChange: (orgId: string) => void;
}

const BadgeSquare = ({ badge, color }: { badge: string; color: string }) => (
  <span style={{
    alignItems: 'center',
    background: color,
    borderRadius: 'var(--mgo-radius-sm)',
    color: '#fff',
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: '9.5px',
    fontWeight: 700,
    height: '24px',
    justifyContent: 'center',
    width: '24px',
  }}>
    {badge}
  </span>
);

export const OrgSwitcher = ({ orgs, activeOrgId, orgIds = Object.keys(orgs), onChange }: OrgSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const activeOrg = orgs[activeOrgId];

  useEffect(() => {
    if (!open) return;
    const list = listboxRef.current;
    if (list) {
      const selected = list.querySelector<HTMLElement>('[aria-selected="true"]');
      (selected ?? list.querySelector<HTMLElement>('[role="option"]'))?.focus();
    }
    const handleDocKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', handleDocKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleDocKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  const handleSelect = (orgId: string) => {
    if (orgId !== activeOrgId) onChange(orgId);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, orgId: string, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(orgId);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const options = listboxRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
      options?.[Math.min(index + 1, (options.length ?? 1) - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const options = listboxRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
      options?.[Math.max(index - 1, 0)]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      listboxRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const options = listboxRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
      if (options?.length) options[options.length - 1].focus();
    }
  };

  return (
    <div ref={containerRef} style={{ display: 'inline-block', position: 'relative' }}>
      <button
        ref={triggerRef}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
        style={{
          alignItems: 'center',
          background: 'var(--mgo-color-bg-raised)',
          border: '1px solid var(--mgo-color-grey-05)',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          gap: 'var(--mgo-space-20)',
          padding: 'var(--mgo-space-20) var(--mgo-space-30)',
        }}
        type="button"
      >
        {activeOrg && <BadgeSquare badge={activeOrg.badge} color={activeOrg.color} />}
        <span style={{ color: 'var(--mgo-color-fg-neutral-primary)', fontSize: 'var(--mgo-font-size-sm)', fontWeight: 700 }}>
          {activeOrg?.label ?? 'Select an organisation'}
        </span>
        <svg
          aria-hidden
          fill="none"
          height="16"
          stroke="var(--mgo-color-fg-neutral-secondary)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          style={{ flexShrink: 0, marginLeft: 'var(--mgo-space-10)' }}
          viewBox="0 0 24 24"
          width="16"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul
          ref={listboxRef}
          aria-label="Select organisation"
          role="listbox"
          style={{
            background: 'var(--mgo-color-bg-raised)',
            border: '1px solid var(--mgo-color-border-neutral-soft)',
            borderRadius: 'var(--mgo-radius-md)',
            boxShadow: 'var(--mgo-shadow-md)',
            listStyle: 'none',
            margin: 0,
            minWidth: '200px',
            padding: 'var(--mgo-space-10)',
            position: 'absolute',
            top: 'calc(100% + var(--mgo-space-10))',
            zIndex: 50,
          }}
        >
          {orgIds.map((orgId, index) => {
            const org = orgs[orgId];
            const isActive = orgId === activeOrgId;
            return (
              <li
                aria-selected={isActive}
                key={orgId}
                role="option"
                tabIndex={0}
                style={{
                  alignItems: 'center',
                  borderRadius: 'var(--mgo-radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 'var(--mgo-space-20)',
                  padding: 'var(--mgo-space-20)',
                }}
                onClick={() => handleSelect(orgId)}
                onKeyDown={(e) => handleOptionKeyDown(e, orgId, index)}
              >
                <BadgeSquare badge={org.badge} color={org.color} />
                <span style={{ color: 'var(--mgo-color-fg-neutral-primary)', flex: 1, fontSize: 'var(--mgo-font-size-sm)' }}>
                  {org.label}
                </span>
                {isActive && <span aria-hidden style={{ color: 'var(--mgo-color-fg-brand-primary)', fontSize: '12px' }}>✓</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
